import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { CaretUpDown, Check } from "@phosphor-icons/react";

/** Pick the pool that best explains the current selection (for edit wizards). */
export function inferPoolId(pools, servers, selectedIds) {
  const selected = new Set(selectedIds || []);
  if (!selected.size) {
    return (pools || []).find((p) => p.is_default)?.id || "";
  }
  let best = "";
  let bestScore = -1;
  for (const p of pools || []) {
    const members = (servers || []).filter((s) => s.pool_id === p.id).map((s) => s.id);
    const memberSet = new Set(members);
    if (![...selected].every((id) => memberSet.has(id))) continue;
    const score = members.filter((id) => selected.has(id)).length;
    if (score > bestScore) {
      bestScore = score;
      best = p.id;
    }
  }
  return best;
}

/**
 * Smart-search pool dropdown + per-server checkboxes.
 * Choosing a pool selects all eligible servers in that pool; admin can uncheck individuals.
 */
export default function PoolServerAssign({
  label,
  hint,
  pools = [],
  servers = [],
  value = [],
  onChange,
  filterServer,
  secondaryText,
  allowEmpty = false,
  emptyHint,
  showDefaultLabel, // undefined = auto (hide for reseller/customer)
  testIdPrefix = "pool-assign",
  className = "",
}) {
  const { user } = useAuth();
  const showDefault = showDefaultLabel ?? !["reseller", "customer"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [poolId, setPoolId] = useState("");

  const eligible = useMemo(() => {
    const list = Array.isArray(servers) ? servers : [];
    return filterServer ? list.filter(filterServer) : list;
  }, [servers, filterServer]);

  const usablePools = useMemo(() => {
    const withMembers = (pools || []).filter((p) =>
      eligible.some((s) => s.pool_id === p.id));
    // Keep pools that have members; if none matched but pools exist, still show them
    return withMembers.length ? withMembers : (pools || []);
  }, [pools, eligible]);

  // Sync inferred pool when value/pools load (edit) or when selection cleared
  useEffect(() => {
    const inferred = inferPoolId(usablePools, eligible, value);
    setPoolId((prev) => {
      if (prev && usablePools.some((p) => p.id === prev)) {
        // Keep current pool if selection is still a subset of it
        const members = new Set(eligible.filter((s) => s.pool_id === prev).map((s) => s.id));
        if (!(value || []).length || (value || []).every((id) => members.has(id))) {
          return prev;
        }
      }
      return inferred;
    });
  }, [usablePools, eligible, value]);

  const poolServers = useMemo(() => {
    if (!poolId) {
      // No pool chosen — show currently selected servers so legacy mixes stay editable
      const selected = new Set(value || []);
      return eligible.filter((s) => selected.has(s.id));
    }
    return eligible.filter((s) => s.pool_id === poolId);
  }, [eligible, poolId, value]);

  const selectedPool = usablePools.find((p) => p.id === poolId);
  const display = selectedPool
    ? `${selectedPool.name}${showDefault && selectedPool.is_default ? " (default)" : ""}`
    : (value || []).length
      ? "Custom selection"
      : "Select pool…";

  const pickPool = (id) => {
    setPoolId(id);
    setOpen(false);
    if (!id) {
      if (allowEmpty) onChange([]);
      return;
    }
    const ids = eligible.filter((s) => s.pool_id === id).map((s) => s.id);
    onChange(ids);
  };

  const toggle = (sid, checked) => {
    const cur = new Set(value || []);
    if (checked) cur.add(sid);
    else cur.delete(sid);
    onChange([...cur]);
  };

  const sec = secondaryText || ((s) => s.host || s.public_ip || "");

  return (
    <div className={`space-y-2 ${className}`} data-testid={`${testIdPrefix}-wrap`}>
      <Label>
        {label}
        {hint ? <span className="text-muted-foreground text-xs font-normal"> {hint}</span> : null}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            data-testid={`${testIdPrefix}-pool`}
            className="w-full justify-between font-normal h-9 px-3"
          >
            <span className="truncate">{display}</span>
            <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search pools…" data-testid={`${testIdPrefix}-pool-search`} />
            <CommandList>
              <CommandEmpty>No pool found.</CommandEmpty>
              <CommandGroup>
                {allowEmpty && (
                  <CommandItem
                    value="__none__ clear pool"
                    data-testid={`${testIdPrefix}-pool-none`}
                    onSelect={() => pickPool("")}
                  >
                    <Check className={`mr-2 h-3.5 w-3.5 ${!poolId ? "opacity-100" : "opacity-0"}`} />
                    <span className="text-muted-foreground">None (any healthy)</span>
                  </CommandItem>
                )}
                {usablePools.map((p) => (
                  <CommandItem
                    key={p.id}
                    value={`${p.name}${showDefault && p.is_default ? " default" : ""}`}
                    data-testid={`${testIdPrefix}-pool-option-${p.id}`}
                    onSelect={() => pickPool(p.id)}
                  >
                    <Check className={`mr-2 h-3.5 w-3.5 ${poolId === p.id ? "opacity-100" : "opacity-0"}`} />
                    {p.name}
                    {showDefault && p.is_default ? (
                      <span className="ml-1 text-muted-foreground text-xs">default</span>
                    ) : null}
                    <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                      {eligible.filter((s) => s.pool_id === p.id).length}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div
        className="space-y-1.5 rounded-md border border-border p-2.5 max-h-40 overflow-y-auto"
        data-testid={`${testIdPrefix}-list`}
      >
        {poolServers.map((s) => (
          <label key={s.id} className="flex items-center gap-2 text-sm">
            <Checkbox
              data-testid={`${testIdPrefix}-${s.id}`}
              checked={(value || []).includes(s.id)}
              onCheckedChange={(v) => toggle(s.id, !!v)}
            />
            {s.name}{" "}
            <span className="text-muted-foreground text-xs">{sec(s)}</span>
          </label>
        ))}
        {poolServers.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {emptyHint || (poolId ? "No servers in this pool." : "Select a pool to assign servers.")}
          </p>
        )}
      </div>
    </div>
  );
}

/** Ids of eligible servers in the default pool (create-wizard preselect). */
export function defaultPoolServerIds(pools, servers, filterServer) {
  const def = (pools || []).find((p) => p.is_default) || (pools || [])[0];
  if (!def) return [];
  const list = filterServer ? (servers || []).filter(filterServer) : (servers || []);
  return list.filter((s) => s.pool_id === def.id).map((s) => s.id);
}
